import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { EnvelopeIcon, BellIcon, DevicePhoneMobileIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';

const NotificationNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const notificationType = data.config?.type || 'email';
  
  // Icon kiválasztása típus alapján
  const getIcon = (type: string) => {
    switch (type) {
      case 'email': return EnvelopeIcon;
      case 'sms': return DevicePhoneMobileIcon;
      case 'push': return BellIcon;
      case 'slack': return ChatBubbleLeftIcon;
      default: return EnvelopeIcon;
    }
  };

  // Típus emoji
  const getTypeEmoji = (type: string) => {
    switch (type) {
      case 'email': return '📧';
      case 'sms': return '📱';
      case 'push': return '🔔';
      case 'slack': return '💬';
      default: return '📧';
    }
  };

  const Icon = getIcon(notificationType);

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-red-400 to-red-500 border-2 transition-all duration-200 ${
      selected ? 'border-red-600 shadow-xl scale-105' : 'border-red-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-red-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Notification type with emoji */}
          <div className="flex items-center mt-1 space-x-1">
            <span className="text-sm">{getTypeEmoji(notificationType)}</span>
            <span className="text-xs text-red-100 capitalize">{notificationType}</span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-700' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Configuration preview */}
      <div className="mt-2 space-y-1">
        {/* Címzett/Cél megjelenítése */}
        {(data.config?.recipient || data.config?.phoneNumber || data.config?.channel) && (
          <div className="text-xs text-red-100 bg-black bg-opacity-20 px-2 py-1 rounded">
            <strong>
              {notificationType === 'email' ? 'Címzett:' : 
               notificationType === 'sms' ? 'Tel:' : 
               notificationType === 'slack' ? 'Csatorna:' : 'Cél:'}
            </strong>
            <span className="ml-1 truncate">
              {data.config.recipient || data.config.phoneNumber || data.config.channel || 'Nincs beállítva'}
            </span>
          </div>
        )}
        
        {/* Tárgy/Cím megjelenítése */}
        {(data.config?.subject || data.config?.title) && (
          <div className="text-xs text-red-100 bg-black bg-opacity-20 px-2 py-1 rounded">
            <strong>{notificationType === 'push' ? 'Cím:' : 'Tárgy:'}</strong>
            <span className="ml-1 truncate">{data.config.subject || data.config.title}</span>
          </div>
        )}
        
        {/* Üzenet előnézet */}
        {data.config?.message && (
          <div className="text-xs text-red-100 bg-black bg-opacity-20 px-2 py-1 rounded">
            <strong>Üzenet:</strong>
            <div className="truncate mt-1 opacity-90">
              {data.config.message.length > 50 
                ? `${data.config.message.substring(0, 50)}...` 
                : data.config.message}
            </div>
            {notificationType === 'sms' && (
              <div className="text-xs opacity-75 mt-1">
                {data.config.message.length}/160 karakter
              </div>
            )}
          </div>
        )}
        
        {/* Template info */}
        {data.config?.template && (
          <div className="text-xs text-red-100 bg-black bg-opacity-20 px-2 py-1 rounded">
            <strong>Sablon:</strong>
            <span className="ml-1 capitalize">{data.config.template}</span>
          </div>
        )}
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-700 bg-opacity-30 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Delivery status */}
      {data.executionData && (
        <div className="mt-2 text-xs text-red-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              data.executionData.success ? 'bg-green-400' : 'bg-red-400'
            }`} />
            <span>
              {data.executionData.success ? 'Elküldve' : 'Sikertelen'}
            </span>
          </div>
          {data.executionData.messageId && (
            <div className="font-mono text-xs mt-1">
              ID: {data.executionData.messageId}
            </div>
          )}
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-red-600 border-2 border-white"
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-red-600 border-2 border-white"
      />
    </div>
  );
};

export default NotificationNode;
